import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Names } from '@ils/static/names.js';
import { scoreTransformBeforeUpdate } from '../server/resolver/scores.js';

export type ScoreStatus = 'succeeded' | 'failed' | 'consens';

@Entity()
export class Score extends MongoEntity {
  static override _class = Names.Score._class;
  static override _collectionName = Names.Score._collectionName;

  constructor(doc: PartialEntityData<Score>) {
    super(doc);
  }

  @validate(nonOptional())
  @Field(type => String)
  examDayID!: string;

  @validate(nonOptional())
  @Field(type => String)
  candidateID!: string;

  @validate(nonOptional())
  @Field(type => String)
  status!: ScoreStatus;

  @validate(nonOptional())
  @Field(type => [SlotScore], { default: v => v ?? [] })
  slotScores!: SlotScore[];

  static override async beforeCreate(entity: Score) {
    await this.transformBeforeUpdate(entity, entity);
  }

  static override async transformBeforeUpdate(oldEntity: PartialEntityData<Score>, update: Score) {
    await scoreTransformBeforeUpdate(oldEntity, update);
  }

  isRatingComplete() {
    return this.slotScores.every(slotScore => slotScore.score !== -1);
  }

  getTotalScore() {
    return this.slotScores.reduce((acc, slotScore) => acc + Math.max(slotScore.score, 0), 0) / this.slotScores.length;
  }

  isOSCEGroupRated(osceGroupID: string) {
    return this.slotScores
      .filter(slotScore => slotScore.osceGroupID === osceGroupID)
      .every(slotScore => slotScore.score !== -1);
  }

  getOSCEGroupScore(osceGroupID: string) {
    const slotScores = this.slotScores.filter(slotScore => slotScore.osceGroupID === osceGroupID);
    return slotScores.reduce((acc, curr) => acc + Math.max(curr.score, 0), 0) / slotScores.length;
  }
}

@Entity()
export class SlotScore extends AdornisEntity {
  static override _class = Names.SlotScore._class;

  constructor(doc: PartialEntityData<SlotScore>) {
    super(doc);
  }

  static override resolveID(entity: any): string {
    return entity.slotID;
  }

  @Field(type => String)
  linguistID!: string;

  @Field(type => String)
  doctorID!: string;

  @validate(nonOptional())
  @Field(type => String)
  slotID!: string;

  @validate(nonOptional())
  @Field(type => String)
  osceID!: string;

  @Field(type => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  @Field(type => Date, { default: v => v ?? new Date() })
  updatedAt!: Date;

  @Field(type => String)
  osceGroupID?: string;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Float)
  score!: number;
}
